import type { Flights } from "@b2bportal/air-shopping-api";
import type { FlightSearchValues } from "@b2bportal/core-flights";
import {
  type FlightShopRequest,
  PackageEndpointsV2,
  type BundledPackageStateToFareId,
  type PackagesCustomizeFlightsShopRequest,
  type PackagesCustomizeFlightsShopResponse,
  type PackageSearchQueryParams,
} from "@b2bportal/core-packages";
import { getDeviceType } from "@b2bportal/core-utilities";
import { axiosInstance } from "@hopper-b2b/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { FlightShopActions } from "../../../flights/shop/store/slice";
import { PackagesBaseActions } from "../../slice/packages";
import type { RootState } from "@/store";
export type FetchPackageFlightsFulfillResponse = {
  flights: Flights;
  packageStateByFareId: BundledPackageStateToFareId;
};

export interface PackageFlightsProps {
  search?: FlightShopRequest;
  packageToken: string;
}

export const fetchPackageFlights = createAsyncThunk<
  FetchPackageFlightsFulfillResponse,
  { props: PackageFlightsProps },
  { state: RootState; rejectValue: { error: string } }
>("packages/fetchFlights", async ({ props }, thunkAPI) => {
  const { isDesktopAndUp } = getDeviceType();
  const { search, packageToken } = props;

  // TODO(Nate): Use from props
  const availabilityRequest: PackagesCustomizeFlightsShopRequest = {
    customize: {
      bundledPackageStateToken: { value: packageToken },
      flightShopRequest: search,
    },
  };

  try {
    thunkAPI.dispatch(FlightShopActions.setFetchFlightsPending());

    const {
      data: { flightShop, chosenProducts },
    }: { data: PackagesCustomizeFlightsShopResponse } =
      await axiosInstance.post(
        PackageEndpointsV2.Customize,
        availabilityRequest
      );

    // The departure page abandons its shop on unmount. A late response must
    // not reach the core flight shop: the user may already be in a new search.
    if (thunkAPI.signal.aborted) {
      return thunkAPI.rejectWithValue({ error: "abandoned" });
    }

    const currBenchmarkProducts =
      thunkAPI.getState().packages.base.benchmarkProducts?.chosenProducts ?? [];

    // Deduplicate based on ChosenProduct discriminator
    const existingProductTypes = new Set(
      currBenchmarkProducts.map((p) => p.ChosenProduct)
    );
    const newProducts = chosenProducts.filter(
      (p) => !existingProductTypes.has(p.ChosenProduct)
    );

    thunkAPI.dispatch(
      PackagesBaseActions.setBenchmarkProducts({
        chosenProducts: [...currBenchmarkProducts, ...newProducts],
      })
    );

    // Map to flight store
    thunkAPI.dispatch(
      FlightShopActions.setFetchFlightsSuccess({
        flights: flightShop.payload.summarizeResponse.flights,
        prediction: undefined,
        trackingProperties: {},
      })
    );

    return {
      flights: flightShop.payload.summarizeResponse.flights,
      packageStateByFareId: flightShop.packageStateByFareId,
    };
  } catch (error) {
    // Reject the thunk with an error message
    if (!thunkAPI.signal.aborted) {
      thunkAPI.dispatch(FlightShopActions.setFetchFlightsRejected());
    }
    return thunkAPI.rejectWithValue({ error: String(error) });
  }
});
